import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkout } from '../context/WorkoutContext';
import { muscleGroupNames, sampleWorkouts } from '../data/exerciseData';
import './ExerciseSelector.css';

const ExerciseSelector = () => {
    const navigate = useNavigate();
    const { state, actions } = useWorkout();
    const [userInput, setUserInput] = useState(state.selectedMuscleGroups || '');

    // Define the allowed characters for input
    const allowedChars = "~w@d^_ekhmatfgvpqnujyzbilcors!$x&*()-+=[]{}:;|<>";

    // Handle input change with validation
    const handleInputChange = (e) => {
        const input = e.target.value.toLowerCase();
        // Only allow valid characters and prevent three consecutive same characters
        const validInput = input.split('').filter(char => {
            return allowedChars.includes(char);
        }).join('');

        // Check for three consecutive same characters
        let result = '';
        for (let i = 0; i < validInput.length; i++) {
            const char = validInput[i];
            const lastThreeChars = result.slice(-2) + char;
            if (lastThreeChars.length < 3 || !(lastThreeChars[0] === lastThreeChars[1] && lastThreeChars[1] === lastThreeChars[2])) {
                result += char;
            }
        }

        setUserInput(result);
    };

    // Handle muscle group button click
    const handleMuscleGroupClick = (char) => {
        // Same validation as handleInputChange
        const newInput = userInput + char;
        const lastThreeChars = newInput.slice(-3);
        if (lastThreeChars.length < 3 || !(lastThreeChars[0] === lastThreeChars[1] && lastThreeChars[1] === lastThreeChars[2])) {
            setUserInput(newInput);
        }
    };

    // Handle sample workout selection
    const handleSampleWorkoutClick = (workout) => {
        setUserInput(workout.code);
    };

    // Handle next button click
    const handleNext = () => {
        if (userInput.trim() !== '') {
            actions.setMuscleGroups(userInput);
            navigate('/rep-count');
        }
    };

    // Group muscle groups into categories for UI
    const muscleGroupCategories = {
        'Bodyweight Basics': ['~', 'w', '@', 'd', '^', '_'],
        'Advanced Bodyweight': ['e', 'k', 'h', 'm', 'a', 't', 'f', 'g'],
        'Specific Muscles': ['i', 'v', 'p', 'q', 'n', 'u', 'j', 'y', 'z'],
        'Band Exercises': ['l', 'c', 'b', 'o', 'r', 's'],
        'Gym Exercises': ['$', 'x', '&', '*', '(', ')', '-', '+', '=', '[', ']', '{', '}', ':', ';', '|', '<', '>'],
        'Full Body': ['!']
    };

    return (
        <div className="exercise-selector-container">
            <header className="selector-header">
                <button className="back-button" onClick={() => navigate('/')}>
                    Back
                </button>
                <h1>Select Muscle Groups</h1>
            </header>

            <div className="input-section">
                <div className="input-field">
                    <label htmlFor="muscleGroups">Muscle Group Codes:</label>
                    <input
                        type="text"
                        id="muscleGroups"
                        value={userInput}
                        onChange={handleInputChange}
                        placeholder="Example: ~w@d^_"
                    />
                </div>

                <div className="legend">
                    <div className="legend-item">~ = legs</div>
                    <div className="legend-item">w = chest</div>
                    <div className="legend-item">@ = back</div>
                    <div className="legend-item">d = core</div>
                    <div className="legend-item">^ = arms</div>
                    <div className="legend-item">_ = shoulders</div>
                </div>

                <button
                    className="next-button"
                    onClick={handleNext}
                    disabled={userInput.trim() === ''}
                >
                    Next
                </button>
            </div>

            <div className="muscle-groups-section">
                <h2>Select Muscle Groups</h2>

                {Object.entries(muscleGroupCategories).map(([category, groups]) => (
                    <div key={category} className="category-section">
                        <h3>{category}</h3>
                        <div className="muscle-group-buttons">
                            {groups.map(group => (
                                <button
                                    key={group}
                                    className="muscle-group-button"
                                    onClick={() => handleMuscleGroupClick(group)}
                                >
                                    <span className="muscle-code">{group}</span>
                                    <span className="muscle-name">{muscleGroupNames[group]}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className="sample-workouts-section">
                <h2>Sample Workouts</h2>
                <div className="sample-workouts-list">
                    {sampleWorkouts.map((workout, index) => (
                        <div
                            key={index}
                            className="sample-workout-item"
                            onClick={() => handleSampleWorkoutClick(workout)}
                        >
                            <span className="workout-code">{workout.code}</span>
                            <span className="workout-name">{workout.name}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ExerciseSelector;